import { useEffect, useRef } from 'react';
import { mergeLiveRecord } from './mergeLiveRecord.mjs';

export default function usePersonLiveUpdates(options) {
  const latest = useRef(options);
  latest.current = options;
  const saved = useRef(null);
  // Compact lists do not contain a baseline for the full editor. Capture the
  // loaded record on selection, before the user begins editing its fields.
  if (String(saved.current?.id) !== String(options.form?.id) || (saved.current?._compact && !options.form?._compact)) {
    saved.current = options.form?.id ? JSON.parse(JSON.stringify(options.form)) : null;
  }
  const { service, eventName } = options;
  useEffect(() => {
    let stopped = false;
    let running = false;
    let revision;
    let timer;
    const refresh = async (provided) => {
      const before = latest.current;
      const list = provided || await (service.listStudents || service.listAttendees)({ compact: true });
      if (stopped || latest.current.saving) return false;
      const selectedId = latest.current.form?.id;
      const selected = list.find((item) => String(item.id) === String(selectedId));
      const getRecord = service.getStudent || service.getAttendee;
      const detailed = selected && getRecord
        ? await (service.getStudent
          ? service.getStudent(selected.id, latest.current.form?._filesLoaded ? {} : { excludeFiles: true })
          : getRecord(selected.id))
        : selected;
      if (stopped || latest.current.saving) return false;
      const ordered = before.sortRecords(list);
      const previous = saved.current;
      before.setRecords((current) => ordered.map((item) => {
        if (detailed && String(item.id) === String(detailed.id)) return { ...item, ...detailed, _compact: false };
        const old = current.find((record) => String(record.id) === String(item.id));
        return { ...old, ...item, _compact: Boolean(item._compact) };
      }));
      if (detailed) before.setForm((current) => {
        if (String(current?.id) !== String(detailed.id)) return current;
        const baseline = { ...before.emptyRecord(), ...(String(previous?.id) === String(detailed.id) ? previous : {}) };
        return { ...mergeLiveRecord(current, baseline, { ...before.emptyRecord(), ...detailed }), _compact: false };
      });
      if (detailed && String(latest.current.form?.id) === String(detailed.id)) saved.current = { ...before.emptyRecord(), ...detailed, _compact: false };
      return true;
    };
    const check = async () => {
      if (running || stopped || document.hidden || latest.current.saving) return;
      running = true;
      try {
        const result = await service.getSyncRevision();
        if (!stopped && String(result.revision) !== revision) {
          if (await refresh()) revision = String(result.revision);
        }
      } catch (_) { /* The next revision check retries without clearing the visible data. */ }
      finally { running = false; }
    };
    const tick = async () => { await check(); if (!stopped) timer = window.setTimeout(tick, 2500); };
    const manual = async (event) => {
      if (running || latest.current.saving) { revision = undefined; return; }
      running = true;
      try { await refresh(event?.detail?.students || event?.detail?.attendees); }
      catch (_) { revision = undefined; }
      finally { running = false; }
    };
    timer = window.setTimeout(tick, 1800);
    window.addEventListener(eventName, manual);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    return () => { stopped = true; window.clearTimeout(timer); window.removeEventListener(eventName, manual); window.removeEventListener('focus', check); document.removeEventListener('visibilitychange', check); };
  }, [service, eventName]);
}
